/* eslint-disable react/forbid-prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';

import Forbidden from '~/components/Forbidden';
import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';

import api from '~/services/pluginbot-api';
import { postRobotRegister } from '~/services/robots-register';
import { expireSession } from '~/store/modules/auth/actions';

function randomMockIdentifier() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return `mock-${crypto.randomUUID()}`;
    }
    return `mock-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 11)}`;
}

export default function MockRobotRegister() {
    const dispatch = useDispatch();
    const profile = useSelector(state =>
        state.user && state.user.profile ? state.user.profile : null
    );
    const [types, setTypes] = useState([]);
    const [typeSlug, setTypeSlug] = useState('');
    const [customIdentifier, setCustomIdentifier] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    function requestError(error) {
        if (error.response) {
            const d = error.response.data || {};
            const codeKey = d.code ? `errors.${d.code}` : null;
            const message = codeKey ? (
                <LocaleMessage msg={codeKey} />
            ) : (
                d.error || d.message || `HTTP ${error.response.status}`
            );
            toast.error(message);
            if (error.response.status === 401) {
                dispatch(expireSession());
            }
        } else if (error.request) {
            toast.error(<LocaleMessage msg="errors.request" />);
        } else {
            toast.error(<LocaleMessage msg="errors.unknown" />);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        let cancelled = false;
        async function loadTypes() {
            setIsLoading(true);
            try {
                const { data } = await api.get('types?group=robot');
                if (!cancelled) {
                    setTypes(Array.isArray(data) ? data : []);
                }
            } catch (e) {
                if (!cancelled) requestError(e);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }
        loadTypes();
        return () => {
            cancelled = true;
        };
    }, []);

    if (profile && profile.permission !== 'master') {
        return <Forbidden />;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!typeSlug) {
            toast.error(
                <LocaleMessage msg="page.mock_robot_register.error_no_type" />
            );
            return;
        }
        const identifier = customIdentifier.trim() || randomMockIdentifier();
        setSubmitting(true);
        setResult(null);
        try {
            const { data } = await postRobotRegister({
                identifier,
                type: typeSlug,
            });
            setResult(data);
            if (data && data.message === 'Robot already registered') {
                toast.info(
                    <LocaleMessage msg="page.mock_robot_register.already_registered" />
                );
            } else {
                toast.success(
                    <LocaleMessage msg="page.mock_robot_register.success" />
                );
            }
        } catch (error) {
            requestError(error);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <PageContent
            title={<LocaleMessage msg="page.mock_robot_register.title" />}
            currentPage={<LocaleMessage msg="page.mock_robot_register.title" />}
            breadcrumbs={[
                {
                    url: '/robottypes',
                    title: <LocaleMessage msg="breadcrumbs.robot_types" />,
                },
            ]}
            loading={isLoading}
        >
            <Box maxWidth={560}>
                <Typography variant="body1" paragraph color="textSecondary">
                    <LocaleMessage msg="page.mock_robot_register.description" />
                </Typography>
                <Paper
                    style={{ padding: 24 }}
                    component="form"
                    onSubmit={handleSubmit}
                >
                    <FormControl
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        required
                    >
                        <InputLabel id="mock-robot-type-label">
                            <LocaleMessage msg="page.mock_robot_register.type_label" />
                        </InputLabel>
                        <Select
                            labelId="mock-robot-type-label"
                            value={typeSlug}
                            onChange={ev => setTypeSlug(ev.target.value)}
                            label={
                                <LocaleMessage msg="page.mock_robot_register.type_label" />
                            }
                        >
                            {types.map(t => (
                                <MenuItem
                                    key={t.id}
                                    value={t.slug || String(t.id)}
                                >
                                    {t.name}
                                    {t.slug ? ` (${t.slug})` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        label={
                            <LocaleMessage msg="page.mock_robot_register.identifier_optional" />
                        }
                        helperText={
                            <LocaleMessage msg="page.mock_robot_register.identifier_hint" />
                        }
                        value={customIdentifier}
                        onChange={ev => setCustomIdentifier(ev.target.value)}
                    />
                    <Box mt={2}>
                        <Button
                            type="submit"
                            color="primary"
                            variant="contained"
                            disabled={submitting || !typeSlug}
                        >
                            <LocaleMessage msg="page.mock_robot_register.submit" />
                        </Button>
                    </Box>
                </Paper>
                {result && result.code != null ? (
                    <Box mt={3}>
                        <Typography variant="subtitle1" gutterBottom>
                            <LocaleMessage msg="page.mock_robot_register.result_code" />
                        </Typography>
                        <Paper
                            variant="outlined"
                            style={{
                                padding: 16,
                                fontFamily: 'monospace',
                                wordBreak: 'break-all',
                            }}
                        >
                            {String(result.code)}
                        </Paper>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: 12 }}
                        >
                            <LocaleMessage msg="page.mock_robot_register.result_hint" />
                        </Typography>
                    </Box>
                ) : null}
            </Box>
        </PageContent>
    );
}
